/**
 * EVO — kunlik kurs vidjeti.
 *
 * ============================================================================
 * MIJOZNING ANIQ TALABI
 * ============================================================================
 * «USD kursi kuniga bir marta belgilanadi va DASTUR TEPASIDA turadi.»
 *
 * Nima uchun bu shunchaki bezak emas:
 *   • Sotuv USD, kassa so'm, xarid CNY (DATA_MODEL §1.4)
 *   • Har hujjat o'z sanasidagi kursni MUHRLAB oladi va qayta hisoblanmaydi
 *   • Ya'ni kurs kiritilmagan kunda yozilgan hujjat KEYIN TUZATILMAYDI
 *
 * Shuning uchun «kurs kiritilmagan» holati jimgina o'tkazib yuborilmaydi.
 *
 * ╔══════════════════════════════════════════════════════════════════════════╗
 * ║ NIMA UCHUN ENDI BITTA CHIP: `$ 12 800`                                   ║
 * ║                                                                          ║
 * ║ Egasi: «tepada kurs turibdi — u katta, uzun yozuv. Oddiy `$ 12 800`      ║
 * ║ yetadi».                                                                 ║
 * ║                                                                          ║
 * ║ Ilgari bu yerda TO'RTTA narsa turardi: «Kunlik kurs» sarlavhasi,         ║
 * ║ «1 USD = 12 800 so'm» qatori, sana va yuan qatori. Bu ~340 px va u       ║
 * ║ yuqori panelning yarmini yeb turardi. Har kuni bir xil turadigan         ║
 * ║ matnni odam ikkinchi kundan boshlab O'QIMAYDI — u faqat RAQAMGA          ║
 * ║ qaraydi. Ya'ni qolgan hammasi joy egallardi, ma'lumot bermasdi.          ║
 * ║                                                                          ║
 * ║ Endi ekranda faqat raqam turadi, tushuntirish esa `title` da: u          ║
 * ║ kerak bo'lganda chiqadi va hech qachon joy egallamaydi.                  ║
 * ╚══════════════════════════════════════════════════════════════════════════╝
 *
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ SANA NIMA UCHUN OLIB TASHLANDI (va nima uni ALMASHTIRDI)                  │
 * │                                                                           │
 * │ Avvalgi mulohaza: «qaysi kunning kursi ekanini bilmasdan raqamga ishonib  │
 * │ bo'lmaydi». U TO'G'RI edi, lekin sanani DOIM ko'rsatish orqali hal        │
 * │ qilinishi shart emas: kurs bugungi bo'lsa, sana hech qanday yangilik      │
 * │ bermaydi — u har kuni «bugun» deb turadi va shu sababli o'qilmay qoladi.  │
 * │                                                                           │
 * │ Xavf faqat BITTA holatda: kurs bugungi EMAS. Endi aynan o'sha holat       │
 * │ RANG bilan ajratiladi (sariq chip + ⚠) va sana `title` ichida qoladi.     │
 * │ Ya'ni ogohlantirish kuchaydi, doimiy shovqin esa yo'qoldi.                │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ YUAN TEPADA YO'Q — LEKIN BAZADA VA TAHRIRDA BOR                           │
 * │                                                                           │
 * │ Egasi: «yuan kerak emas, bazada bo'lsa bo'ldi — xitoy ta'minotchilari     │
 * │ uchun».                                                                   │
 * │                                                                           │
 * │ UZS — kunlik ishning kursi: sotuv USD da yoziladi, mijoz so'mda to'laydi. │
 * │ U yo'q bo'lsa kunlik ish TO'XTAYDI.                                       │
 * │                                                                           │
 * │ CNY — xarid kursi va u KUNLIK EMAS: konteyner oyda bir-ikki marta         │
 * │ keladi. Uni yuqori panelda har kuni ko'rsatish — 100 xodimning 98 tasiga  │
 * │ hech qachon kerak bo'lmaydigan raqamni ko'rsatish demak.                  │
 * │                                                                           │
 * │ ⚠️ Lekin u OLIB TASHLANMADI: kurs kiritish formasida yuan maydoni         │
 * │ o'z joyida turadi (`fx.cnyLabel`) va `/fx-rates` ekranida ham. Ya'ni      │
 * │ xaridchi konteyner hujjatini yozishdan oldin uni SHU YERDAN kirita        │
 * │ oladi — faqat qolgan 98 xodimning ekranidan ketdi.                        │
 * └───────────────────────────────────────────────────────────────────────────┘
 */
import { useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isApiError } from '../../lib/api';
import { usePermission } from '../../lib/auth';
import { formatDate, formatRate, todayIso } from '../../lib/format';
import {
  DAILY_RATE_QUERY_KEY,
  fetchDailyRate,
  setDailyRate,
  type RateCurrency,
} from '../../lib/fx';
import { useT } from '../../lib/i18n-context';
import { Alert } from '../ui/alert';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Skeleton } from '../ui/skeleton';
import { Spinner } from '../ui/spinner';

/**
 * Chipning umumiy shakli.
 *
 * ╔══════════════════════════════════════════════════════════════════════════╗
 * ║ BALANDLIK 36 px (`h-9`) — VA BU IZOH ILGARI YOLG'ON EDI                  ║
 * ║                                                                          ║
 * ║ Bu yerda «`h-8` — yonidagi til segmentlari va filial yorlig'i bilan      ║
 * ║ AYNAN bir xil» deb yozilgan edi. O'lchandi — hech biri bir xil emas:     ║
 * ║                                                                          ║
 * ║   til segmentlari   24 px   (p-0.5 + py-1 + `text-xs leading-none`)      ║
 * ║   filial yorlig'i   32 px   (`h-8`)                                      ║
 * ║   kurs chipi        32 px   (`h-8`)                                      ║
 * ║   qo'ng'iroq/mavzu  36 px   (`size-9`)                                   ║
 * ║   avatar            32 px   (`size-8`)                                   ║
 * ║                                                                          ║
 * ║ Ya'ni bitta qatorda TO'RT xil balandlik turardi va izohning o'zi buni    ║
 * ║ «bir xil» deb e'lon qilgani uchun hech kim tekshirmasdi. Egasining       ║
 * ║ «kichkina joylari tor» degan mulohazasi aynan shu: eng kichigi (til)     ║
 * ║ eng kattasidan bir yarim baravar past edi.                              ║
 * ║                                                                          ║
 * ║ Endi hammasi 36 px. Nima uchun 32 emas, 36: qatordagi yarmi TUGMA        ║
 * ║ (qo'ng'iroq, mavzu, chiqish) va ular allaqachon 36 px edi — pastga       ║
 * ║ tekislash bosish nishonini kichraytirardi. Omborchi menyuga planshetda   ║
 * ║ barmoq bilan tegadi (`sidebar.tsx` izohi), 32 px esa barmoq uchun kam.   ║
 * ╚══════════════════════════════════════════════════════════════════════════╝
 */
const CHIP_BASE =
  'inline-flex h-9 shrink-0 items-center gap-1.5 rounded-lg border px-2.5 text-sm font-semibold';

export function FxRateWidget(): ReactNode {
  const t = useT();

  // Kursni kim tahrirlay oladi: admin va finance (PERMISSION_MATRIX §3.5).
  // Manager ham ko'radi, lekin O'ZGARTIRA OLMAYDI — chip bosilmaydigan
  // yorliqqa aylanadi va tahrir oynasi umuman render qilinmaydi.
  const canEdit = usePermission('fxrate.edit');
  const [editOpen, setEditOpen] = useState(false);

  const { data, isPending, isError, refetch } = useQuery({
    queryKey: DAILY_RATE_QUERY_KEY,
    queryFn: fetchDailyRate,
    // Kurs kuniga bir marta o'zgaradi — har fokusda qayta so'rash ortiqcha trafik.
    staleTime: 5 * 60 * 1000,
  });

  if (isPending) {
    /*
     * Skelet, spinner emas — va AYNAN chip o'lchamida.
     *
     * Kurs kelganda yonidagi til segmentlari va foydalanuvchi bloki
     * joyidan siljimasligi kerak: panel tepada turadi va uning
     * «sakrashi» butun ekranni qimirlagandek ko'rsatadi.
     */
    /* `evo-skeleton-chrome` — korpus ustidagi variant (`styles.css` izohi). */
    return <Skeleton className="evo-skeleton-chrome h-9 w-24 rounded-lg" />;
  }

  if (isError) {
    return (
      <button
        type="button"
        onClick={() => void refetch()}
        title={t('fx.title')}
        /* Korpus tokeni — pastdagi `tone` bilan bir sababdan. */
        className={`${CHIP_BASE} border-transparent bg-chrome-danger text-chrome hover:opacity-90`}
      >
        {t('common.retry')}
      </button>
    );
  }

  const { status, uzs, cny } = data;

  /*
   * UCH HOLAT, UCH RANG. Boshqa hech qanday farq yo'q.
   *
   *   ok      — bugungi kurs bor: xotirjam, neytral chip.
   *   stale   — kurs bor, lekin ESKI: raqam ko'rsatiladi (u hali ham eng
   *             yaxshi ma'lumot), lekin sariq va ⚠ bilan.
   *   missing — umuman kurs yo'q: qizil. Bu KUNLIK ISHNI TO'XTATADIGAN
   *             holat, chunki bugun yozilgan hujjat keyin tuzatilmaydi.
   *
   * Sariq va qizilni bitta rangga birlashtirish RAD ETILDI: «eski kurs»
   * bilan ishlash mumkin (raqam bor, u shunchaki kechagi), «kurs yo'q»
   * bilan esa mumkin emas. Ikkalasi bir xil ko'rinsa, qizil rang har
   * kuni chiqadigan shovqinga aylanardi.
   *
   * ┌─ FAQAT `ok` HOLATI KORPUS RANGIGA O'TDI ─────────────────────────────┐
   * │                                                                      │
   * │ `ok` — chipning NEYTRAL holati, ya'ni u fonga qo'shilib turishi      │
   * │ kerak. Neytral esa fonga bog'liq: yuqori panel jadega o'tgach        │
   * │ `bg-surface-2` neytral bo'lishdan to'xtab, jade ustidagi OQ quti     │
   * │ bo'lib qoldi — kunning 99% ida ekranda turadigan holat aynan shu.    │
   * │                                                                      │
   * │ `stale` va `danger` ham korpus tokenlarida, lekin BOSHQA sababdan:   │
   * │ ular neytral emas, OGOHLANTIRISH va ularning vazifasi fondan         │
   * │ ajralib turish. Shuning uchun ular TO'LDIRILGAN chip bo'lib qoladi,  │
   * │ faqat rangi korpusning o'z shkalasidan.                              │
   * │                                                                      │
   * │ ⚠️ `warn-soft`/`danger-soft` da qoldirish sinaldi va rad etildi:     │
   * │ o'sha ikki token MAVZUGA qarab o'zgaradi (yorug'da pastel, qorong'i  │
   * │ mavzuda deyarli qora), korpus esa mavzudan mustaqil. Natijada butun  │
   * │ ramkadagi yagona element aynan shu chip bo'lardi — kunduzi bir xil,  │
   * │ kechqurun boshqa. Ustiga yorug' mavzudagi `text-warn` (#A24B08)      │
   * │ jade ustida 2.9 — ogohlantirish o'qilmasdi.                          │
   * └──────────────────────────────────────────────────────────────────────┘
   */
  const tone =
    status === 'ok'
      ? 'border-chrome-line bg-chrome-2 text-chrome-ink'
      : status === 'stale'
        ? 'border-transparent bg-chrome-warn text-chrome'
        : 'border-transparent bg-chrome-danger text-chrome';

  const rateDate = uzs === null ? todayIso() : uzs.rateDate;
  const title =
    status === 'missing'
      ? t('fx.missingHint')
      : t('fx.chipTitle', { rate: formatRate(uzs?.rate), date: formatDate(rateDate) });

  /*
   * Chipning ICHI — matn, tugma emas: u ham `<button>` ichida, ham
   * `<span>` ichida bir xil chiqishi kerak (ruxsatga qarab).
   */
  const body =
    status === 'missing' ? (
      <>
        <span aria-hidden="true">⚠</span>
        <span className="whitespace-nowrap">{t('fx.missingShort')}</span>
      </>
    ) : (
      <>
        {status === 'stale' && <span aria-hidden="true">⚠</span>}
        {/*
         * `$` — «1 USD =» ning qisqartmasi. Muted rangda, chunki u har
         * kuni bir xil: ko'z raqamga tushishi kerak, belgiga emas.
         *
         * Muted bu yerda `chrome-muted`: shart faqat `ok` holatida rost va
         * o'sha holatda chip korpus rangida turadi. Ogohlantirish
         * holatlarida esa belgi chipning O'Z rangini meros qiladi.
         */}
        <span className={status === 'ok' ? 'font-normal text-chrome-muted' : ''}>$</span>
        <span className="evo-num">{formatRate(uzs?.rate)}</span>
      </>
    );

  return (
    <div className="relative">
      {canEdit ? (
        /*
         * Chipning O'ZI tahrirni ochadi — alohida «Kursni kiritish» tugmasi
         * OLIB TASHLANDI.
         *
         * U ~120 px egallardi va u yoqadigan oyna baribir chipning ostidan
         * chiqardi, ya'ni ikkita element bitta ishni qilardi. Endi nishon
         * kattaroq (butun chip) va panel qisqaroq. Tugmaning nomi `title`
         * va `aria-label` da saqlandi: ekran o'quvchi «12 800» emas,
         * «Bugungi kursni kiritish» deb o'qiydi.
         */
        <button
          type="button"
          onClick={() => setEditOpen((open) => !open)}
          aria-expanded={editOpen}
          aria-label={`${t('fx.editTitle')} — ${title}`}
          title={`${title}\n${t('fx.editTitle')}`}
          className={`${CHIP_BASE} ${tone} transition-opacity hover:opacity-80 motion-reduce:transition-none`}
        >
          {body}
        </button>
      ) : (
        <span aria-label={`${t('fx.title')} — ${title}`} title={title} className={`${CHIP_BASE} ${tone}`}>
          {body}
        </span>
      )}

      {canEdit && editOpen && (
        <FxRateForm
          uzs={uzs?.rate ?? ''}
          cny={cny?.rate ?? ''}
          rateDate={uzs === null ? null : rateDate}
          isStale={status !== 'ok'}
          onClose={() => setEditOpen(false)}
        />
      )}
    </div>
  );
}

/**
 * Kurs kiritish formasi.
 *
 * Vidjetning OSTIDA ochiladi, alohida sahifa emas: kursni kiritish kun
 * boshidagi bir necha soniyalik ish va uni ekran almashtirishga aylantirish
 * — uni kechiktirishning eng oson yo'li.
 *
 * Har valyuta ALOHIDA yuboriladi (`POST /fx/daily-rate` bitta valyuta oladi):
 * UZS kiritilib CNY xato bo'lsa, UZS baribir saqlanadi. Ikkalasini bitta
 * so'rovga bog'lash kunlik ishni ikkinchi darajali valyuta tufayli to'xtatardi.
 *
 * ⚠️ SANA aynan SHU YERDA ko'rsatiladi. Yuqori paneldan olib tashlangan
 * ma'lumot yo'qolib ketmasligi kerak edi: kursni O'ZGARTIRAYOTGAN odam
 * qaysi kunning raqamini ko'rayotganini bilishi shart, chunki u eski
 * qiymatning ustiga yozadi.
 */
function FxRateForm({
  uzs,
  cny,
  rateDate,
  isStale,
  onClose,
}: {
  readonly uzs: string;
  readonly cny: string;
  readonly rateDate: string | null;
  readonly isStale: boolean;
  readonly onClose: () => void;
}): ReactNode {
  const t = useT();
  const queryClient = useQueryClient();

  const [uzsValue, setUzsValue] = useState(uzs);
  const [cnyValue, setCnyValue] = useState(cny);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: async () => {
      const changed: { currency: RateCurrency; rate: string }[] = [];
      if (uzsValue.trim() && uzsValue.trim() !== uzs) {
        changed.push({ currency: 'UZS', rate: uzsValue.trim() });
      }
      if (cnyValue.trim() && cnyValue.trim() !== cny) {
        changed.push({ currency: 'CNY', rate: cnyValue.trim() });
      }
      // Ketma-ket, parallel EMAS: ikkalasi bitta `daily_rates` qatoriga
      // yozadi va server tomonda upsert poygasiga tushishi mumkin.
      for (const item of changed) await setDailyRate(item);
      return changed.map((c) => c.currency);
    },
    onSuccess: (currencies) => {
      setError(null);
      if (currencies.length === 0) {
        setSaved(t('fx.noChange'));
        return;
      }
      setSaved(t('fx.saved', { currencies: currencies.join(', ') }));
      void queryClient.invalidateQueries({ queryKey: DAILY_RATE_QUERY_KEY });
    },
    onError: (saveError: unknown) => {
      setSaved(null);
      setError(isApiError(saveError) ? saveError.message : t('common.unknownError'));
    },
  });

  return (
    <div className="evo-raised absolute top-full right-0 z-30 mt-2 w-80 rounded-xl border border-line bg-surface p-4">
      <p className="text-sm font-semibold text-ink">{t('fx.editTitle')}</p>

      {/* Qaysi kunning kursi ustiga yozilayotgani — birinchi qatorda. */}
      <p className="mt-0.5 text-xs text-ink-muted">
        {rateDate === null
          ? t('fx.missingShort')
          : isStale
            ? t('fx.stale', { date: formatDate(rateDate) })
            : formatDate(rateDate)}
      </p>

      {/*
       * Ogohlantirish formaning ICHIDA: kurs kiritayotgan odam aynan shu
       * daqiqada uning oqibatini bilishi kerak. Kiritilgandan keyingi
       * hujjatlar shu kursni MUHRLAB oladi va qayta hisoblanmaydi.
       */}
      <div className="mt-3">
        <Alert tone="warn">{t('fx.editHint')}</Alert>
      </div>

      <div className="mt-3 flex flex-col gap-3">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="evo-fx-uzs">{t('fx.uzsLabel')}</Label>
          <Input
            id="evo-fx-uzs"
            inputMode="decimal"
            value={uzsValue}
            onChange={(event) => setUzsValue(event.target.value)}
            placeholder="12800"
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="evo-fx-cny">{t('fx.cnyLabel')}</Label>
          <Input
            id="evo-fx-cny"
            inputMode="decimal"
            value={cnyValue}
            onChange={(event) => setCnyValue(event.target.value)}
            placeholder="7.25"
          />
          {/*
           * Yuan yuqori panelda YO'Q — va bu tushuntirilishi kerak.
           * Xaridchi uni tepada ko'rmay qolganda «o'chirib yuborishgan
           * bo'lsa kerak» deb o'ylab, qo'llab-quvvatlashga qo'ng'iroq
           * qilardi. Bitta qator buni oldini oladi.
           */}
          <p className="text-xs text-ink-muted">{t('fx.cnyHint')}</p>
        </div>

        {error && <Alert tone="danger">{error}</Alert>}
        {saved && <Alert tone="ok">{saved}</Alert>}

        <div className="flex items-center gap-2">
          <Button disabled={save.isPending} onClick={() => save.mutate()}>
            {save.isPending && <Spinner />}
            {t('common.save')}
          </Button>
          <Button variant="secondary" onClick={onClose}>
            {t('common.close')}
          </Button>
        </div>
      </div>
    </div>
  );
}
